import { apiGet, apiPatch, apiPost } from './client.js';
import type { PaginatedMeta } from './tickets.js';

export type InquiryStatus =
  | 'new'
  | 'contacted'
  | 'quoted'
  | 'follow_up'
  | 'converted'
  | 'lost'
  | 'cancelled';

export type InquirySource = 'phone' | 'walk_in' | 'online' | 'email';

export interface QuotedService {
  serviceName: string;
  price: number;
  serviceId?: string;
}

export interface InquiryListItem {
  id: string;
  inquiryNumber: string;
  customerId: string | null;
  customerName: string | null;
  customerPhone: string | null;
  customerEmail: string | null;
  deviceMakeId: string | null;
  deviceModelId: string | null;
  deviceMake: string | null;
  deviceModel: string | null;
  issueDescription: string | null;
  servicesQuoted: QuotedService[];
  quotedTotal: string;
  status: InquiryStatus;
  source: InquirySource | null;
  assignedTo: string | null;
  assignedToName: string | null;
  createdByName: string | null;
  followUpAt: string | null;
  followUpSentAt: string | null;
  convertedTicketId: string | null;
  servicesSummary: string;
  createdAt: string;
  updatedAt: string;
}

export interface InquiryStats {
  total: number;
  byStatus: Record<string, number>;
  conversionRate: number;
  overdueFollowUps: number;
  todayInquiries: number;
  weekInquiries: number;
}

export interface InquiryEvent {
  id: string;
  eventType: string;
  payload: Record<string, unknown>;
  createdAt: string;
  userId: string | null;
}

export interface InquiryDetail extends InquiryListItem {
  notes: string | null;
  followUpNotes: string | null;
  lostReason: string | null;
  customer: {
    id?: string;
    name: string | null;
    phone: string | null;
    email: string | null;
  };
  convertedTicket: {
    id: string;
    ticketNumber: string | null;
    status: string | null;
  } | null;
  events: InquiryEvent[];
}

export const inquiriesApi = {
  stats() {
    return apiGet<InquiryStats>('/api/v1/inquiries/stats');
  },

  list(params?: {
    status?: string;
    source?: InquirySource;
    assignedTo?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
    includeInactive?: boolean;
  }) {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.source) qs.set('source', params.source);
    if (params?.assignedTo) qs.set('assignedTo', params.assignedTo);
    if (params?.search) qs.set('search', params.search);
    if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params?.dateTo) qs.set('dateTo', params.dateTo);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params?.includeInactive) qs.set('includeInactive', 'true');
    const q = qs.toString();
    return apiGet<InquiryListItem[], PaginatedMeta>(`/api/v1/inquiries${q ? `?${q}` : ''}`);
  },

  get(id: string) {
    return apiGet<InquiryDetail>(`/api/v1/inquiries/${id}`);
  },

  create(body: Record<string, unknown>) {
    return apiPost<InquiryListItem>('/api/v1/inquiries', body);
  },

  patch(id: string, body: Record<string, unknown>) {
    return apiPatch(`/api/v1/inquiries/${id}`, body);
  },

  setStatus(id: string, status: InquiryStatus, reason?: string) {
    return apiPost(`/api/v1/inquiries/${id}/status`, { status, reason });
  },

  convert(id: string) {
    return apiPost<{ inquiry: InquiryListItem; ticket: { id: string; ticketNumber: string } }>(
      `/api/v1/inquiries/${id}/convert`,
    );
  },

  followUp(
    id: string,
    body: { method: 'sms' | 'email' | 'phone'; message?: string; followUpAt?: string },
  ) {
    return apiPost(`/api/v1/inquiries/${id}/follow-up`, body);
  },
};
